import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { boolean, fallback, number, object, nullish, string } from "valibot";
import { dataName, label } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { OBJECT_CONDITION_KEYS } from "../../constants";
import FieldValueReference from "../FieldValueReference";
import { defaultStringFilter } from "../../pages/customer/form/filter";
import { getDynamicValueIcon } from "../../utils/menu/optionIconUtil";
import { Toggle } from "../../components/toggle/Toggle";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { keyFromSelector, t } from "i18next";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  required: fallback(boolean(), false),
  remember_input: fallback(boolean(), false),
});
const ValueSchema = object({
  postcode: string(),
  huisnummer: number(),
  toevoeging: nullish(string()),
  straatnaam: string(),
  plaatsnaam: string(),
});

export type ZipCodeFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const formatValueAsString = ({ postcode, huisnummer, toevoeging, straatnaam, plaatsnaam }: ValueType) =>
  `${straatnaam} ${huisnummer}${toevoeging ?? ""}, ${postcode} ${plaatsnaam}`;

const ZipCodeField: Field<ZipCodeFieldProperties, DefaultDtoType, ValueType> = {
  type: "address",
  category: "location",
  widget: "com.moreapps.plugins:zipcode:8",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => {
    const { t } = useTranslation();
    return {
      label_text: t(($) => $.ZIP_CODE_FIELD_LABEL_TEXT),
      required: false,
      remember_input: false,
    };
  },
  ruleBehaviour: {
    conditions: { variant: "text", keys: OBJECT_CONDITION_KEYS },
    actions: { variant: "text" },
  },
  filter: {
    type: "string",
    definition: defaultStringFilter,
  },
  stringValue: formatValueAsString,
  renderValue: formatValueAsString,
  icon: "map",
  titleKey: keyFromSelector(($) => $.ZIP_CODE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.ZIP_CODE_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  valueReferenceOptions: (id, props) =>
    [
      { key: ["postcode"], label: keyFromSelector(($) => $.ZIP_CODE_FIELD_SUB_VALUE_POSTAL_CODE) },
      { key: ["straatnaam"], label: keyFromSelector(($) => $.ZIP_CODE_FIELD_SUB_VALUE_STREET) },
      { key: ["huisnummer"], label: keyFromSelector(($) => $.ZIP_CODE_FIELD_SUB_VALUE_HOUSE_NUMBER) },
      { key: ["plaatsnaam"], label: keyFromSelector(($) => $.ZIP_CODE_FIELD_SUB_VALUE_CITY) },
      { key: ["toevoeging"], label: keyFromSelector(($) => $.ZIP_CODE_FIELD_SUB_VALUE_ADDITION) },
    ].map(({ key, label }) => {
      const referenceId = new FieldValueReference(id, key);
      const icon = getDynamicValueIcon(referenceId, ZipCodeField.type);
      return {
        variant: "dynamicValue",
        value: referenceId,
        label: t(label),
        pill: props.data_name,
        icon,
      };
    }),
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
        <FieldButtonPreview icon="home" text={t(($) => $.ZIP_CODE_FIELD_PREVIEW_ADD_BUTTON_LABEL)} />
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => {
              fieldService.setLabelProperty(props.uid, e.target.value);
            }}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(ZipCodeField);
